import { SettingsSection, UpgradeSubscriptionModal } from '@components'

import { Divider, FormItemsGroup, Space, Spacing, Text, useModal } from '@billy/design-system'
import { Fragment, ReactElement, useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { AutomaticPaymentRemindersType } from '@features/invoicingSettings/modules/paymentsAndReminders'

import { ModalId } from '../../../../../../../../../../enums/modalId'
import { TrackingContext } from '../../../../../../../../../../enums/trackingContext'
import { useComponentId, useFormContext } from '../../../../../../../../../../hooks'
import { useInvoiceSettingsModals } from '../../../../contexts/invoiceSettingsModalsContext'
import { InvoiceSettingsFormSchema } from '../../../../types/invoiceSettingsFormSchema'
import { AutomaticPaymentRemindersRow } from './elements/AutomaticPaymentRemindersRow'
import { IssueDate } from './elements/IssueDate'

const automaticPaymentRemindersTypes: AutomaticPaymentRemindersType[] = Object.values(AutomaticPaymentRemindersType)

interface AutomaticPaymentRemindersProps {
  isLockedByUpsell: boolean
}

export const AutomaticPaymentReminders = ({ isLockedByUpsell }: AutomaticPaymentRemindersProps): ReactElement => {
  const { t } = useTranslation()
  const { automaticPaymentRemindersModal } = useInvoiceSettingsModals()
  const {
    paymentRemindersDetails,
    openModal: openAutomaticPaymentRemindersModal,
    shouldUpdateForm,
    variant: paymentRemindersModalVariant,
  } = automaticPaymentRemindersModal
  const upgradeSubscriptionModalId = useComponentId(ModalId.UpgradeSubscriptionModal)
  const { open: openSubscriptionModal } = useModal(upgradeSubscriptionModalId)
  const { setValue: setInvoiceSettingsFormValue, watch } = useFormContext<InvoiceSettingsFormSchema>()

  const automaticPaymentRemindersSettings = watch('automaticPaymentRemindersSettings')

  const handleEditButtonClick = useCallback(
    (paymentType: AutomaticPaymentRemindersType) => {
      if (isLockedByUpsell) {
        openSubscriptionModal()
        return
      }
      openAutomaticPaymentRemindersModal(paymentType, automaticPaymentRemindersSettings[paymentType])
    },
    [automaticPaymentRemindersSettings, isLockedByUpsell, openAutomaticPaymentRemindersModal, openSubscriptionModal],
  )

  useEffect(() => {
    if (paymentRemindersDetails && shouldUpdateForm) {
      setInvoiceSettingsFormValue(
        `automaticPaymentRemindersSettings.${paymentRemindersModalVariant}`,
        { ...paymentRemindersDetails, active: true },
        { shouldDirty: true },
      )
    }
    // Only re-run when paymentRemindersDetails changes to avoid infinite loop with setInvoiceSettingsFormValue
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentRemindersDetails, shouldUpdateForm])

  return (
    <SettingsSection.Content title={t('settings.organization.external_invoice.automatic_payment_reminders.title')}>
      <Text colorVariant="secondary">
        {t('settings.organization.external_invoice.automatic_payment_reminders.description')}
      </Text>
      <Space size="s16" />
      <FormItemsGroup rowSpacing={Spacing.S16}>
        {automaticPaymentRemindersTypes.map((paymentReminderType) => (
          <Fragment key={paymentReminderType}>
            <AutomaticPaymentRemindersRow
              isLockedByUpsell={isLockedByUpsell}
              openSubscriptionModal={openSubscriptionModal}
              onEdit={handleEditButtonClick}
              paymentRemindersType={paymentReminderType}
            />
            <Divider />
          </Fragment>
        ))}
        <IssueDate isLockedByUpsell={isLockedByUpsell} openSubscriptionModal={openSubscriptionModal} />
      </FormItemsGroup>
      <UpgradeSubscriptionModal
        id={upgradeSubscriptionModalId}
        trackingContext={TrackingContext.AutomaticPaymentReminders}
      />
    </SettingsSection.Content>
  )
}
